'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image';
import { RegionSelector } from 'components/shared/RegionSelector';
import {
  footerThemeConfig,
  DEFAULT_FOOTER_THEME,
  isNoHeaderOrFooterPage,
  isOrderPage,
} from 'components/shared/constants';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { isBloodVisionPartnerPath } from 'lib/blood-vision/partnerRoute';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useRetailStoreLocator } from 'hooks/navigation/useRetailStoreLocator';
import styles from './index.module.css';

function useFooterRoutePath(): string {
  const internalPathname = useInternalPathname();
  return internalPathname.length > 1
    ? internalPathname.replace(/\/$/, '')
    : internalPathname;
}

function getFooterTheme(routePath: string): 'dark' | 'light' {
  if (footerThemeConfig[routePath]) return footerThemeConfig[routePath];
  // Dynamic route, so it can't be a footerThemeConfig key.
  if (isBloodVisionPartnerPath(routePath)) return 'dark';
  return DEFAULT_FOOTER_THEME;
}

/**
 * Client shell around the server-rendered footer content: owns the two
 * route-reactive concerns (per-route theme class, page-based visibility) so
 * the static grid can stay a Server Component in the (site) layout.
 */
export function FooterChrome({ children }: { children: ReactNode }) {
  const routePath = useFooterRoutePath();
  const { liteUI, footerDisabled } = useGlobalUI();

  if (
    liteUI ||
    footerDisabled ||
    isNoHeaderOrFooterPage(routePath) ||
    isOrderPage(routePath)
  ) {
    return null;
  }

  return (
    <footer
      className={`${styles.footerWrapper} ${styles[getFooterTheme(routePath)]}`}
    >
      {children}
    </footer>
  );
}

/** Region selector + retail-store locator (client-fetched, opens a modal). */
export function FooterRegionUtility() {
  const t = useTranslations('common');
  const theme = getFooterTheme(useFooterRoutePath());
  const { hasStores, openStoreLocator } = useRetailStoreLocator('footer');

  if (!hasStores) {
    return <RegionSelector displayFormat="name" theme={theme} />;
  }

  return (
    <div className={styles.footerUtilityRow}>
      <RegionSelector displayFormat="name" theme={theme} />
      <button
        className={styles.findStoreLink}
        data-variant={theme}
        type="button"
        onClick={openStoreLocator}
        data-buttontype="footer find a store"
        aria-label={t('footer.findStore.ariaLabel')}
      >
        {t('footer.findStore.label')}
      </button>
    </div>
  );
}

/** Island because CustomImage's loader fn can't cross the RSC boundary. */
export function FooterHsaBadge() {
  const t = useTranslations('common');
  return (
    <CustomLink href="/hsa-fsa">
      <CustomImage
        src="web_v2/icons/hsa-fsa/hsa-fsa-white-outline.svg"
        alt={t('footer.customImage.alt.hsaFsaEligible')}
        width="248"
        height="81"
        style={{ height: '27px', width: 'auto' }}
      />
    </CustomLink>
  );
}
